import { Box, Typography } from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useMSSP from "../../../../../api/hooks/useMSSP";
import ClearFiltersButton from "../../../../../components/filters/ClearFiltersButton";
import EntityFilter, {
  FILTER_TARGET_TABLE,
} from "../../../../../components/filters/EntityFilter";
import SearchInput from "../../../../../components/filters/SearchInput";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import { useInventoryBootstrap } from "../../../../../contexts/InventoryBootstrapContext";
import { RAW_DATA_DELIMITER } from "../../entitiesTable/fieldUtils";
import {
  getFilters,
  getFiltersMultiTenant,
} from "../../filters/EntityTypeRegularFilters";
import SmartFilterButton from "../../filters/smartFilters/SmartFilterButton";
import { filterName } from "../../filters/utils";
import { useEntities } from "../../hooks/useEntities";
import { ALL_ENTITIES } from "../TableView/EntitiesOverviewTableView";

export default function OverviewFilters({
  fetchedEntities,
  assets,
  appliedFilters,
  setAppliedFilters,
  filterAndOrLogics,
  setFilterAndOrLogics,
  setPostgresFilteredEntities,
  setIsSearching,
  handleToggleTableView,
  entityFilters,
  setEntityFilters,
  entityFilterLogic,
  setEntityFilterLogic,
  onRdsSearchApply,
  onRdsSearchClear,
}) {
  const { getEntitiesRds } = useAPI();
  const { hasMultipleSelectedTenants } = useMSSP();
  const { flattenEntities } = useEntities();
  const [postgresFilters, setPostgresFilters] = useState(entityFilters || []);
  const { isActiveRds } = useContext(EnvFeatureFlagContext);

  const { labels: allLabels, tags: allCustomTags } = useInventoryBootstrap();

  useEffect(() => {
    setPostgresFilters(
      entityFilters && entityFilters.length > 0 ? entityFilters : []
    );
  }, [entityFilters]);

  const filters = useMemo(() => {
    if (isActiveRds) return [];
    const safeAssets = Array.isArray(assets) ? assets : [];
    const safeFetchedEntities = Array.isArray(fetchedEntities)
      ? fetchedEntities
      : [];
    return hasMultipleSelectedTenants
      ? getFiltersMultiTenant({
          entityType: ALL_ENTITIES,
          assets: safeAssets,
          fetchedEntities: safeFetchedEntities,
          appliedFilters: appliedFilters,
          setAppliedFilters: setAppliedFilters,
          filterAndOrLogics: filterAndOrLogics,
          setFilterAndOrLogics: setFilterAndOrLogics,
          allLabels: allLabels,
        })
      : getFilters({
          entityType: ALL_ENTITIES,
          assets: safeAssets,
          fetchedEntities: safeFetchedEntities,
          appliedFilters: appliedFilters,
          setAppliedFilters: setAppliedFilters,
          filterAndOrLogics: filterAndOrLogics,
          setFilterAndOrLogics: setFilterAndOrLogics,
          allLabels: allLabels,
          allCustomTags: allCustomTags,
        });
  }, [
    isActiveRds,
    hasMultipleSelectedTenants,
    assets,
    fetchedEntities,
    appliedFilters,
    setAppliedFilters,
    filterAndOrLogics,
    setFilterAndOrLogics,
    allLabels,
    allCustomTags,
  ]);

  const handlePostgresSearch = async (newFilters, filterLogic = []) => {
    if (!newFilters || newFilters.length === 0) {
      setPostgresFilters([]);
      if (setEntityFilters) {
        setEntityFilters([]);
      }
      if (setEntityFilterLogic) {
        setEntityFilterLogic([]);
      }

      if (setPostgresFilteredEntities) {
        setPostgresFilteredEntities(null);
      }
      if (setIsSearching) {
        setIsSearching(false);
      }
      if (onRdsSearchClear) {
        onRdsSearchClear();
      }
      return;
    }
    handleToggleTableView();

    setPostgresFilters(newFilters);
    if (setEntityFilters) {
      setEntityFilters(newFilters);
    }
    if (setEntityFilterLogic) {
      setEntityFilterLogic(filterLogic);
    }

    if (onRdsSearchApply) {
      onRdsSearchApply(newFilters, filterLogic);
      return;
    }

    if (setIsSearching) {
      setIsSearching(true);
    }

    try {
      const searchRequest = {
        filters: newFilters.map((f) => {
          let processedValue = f.value;
          if (f.value === "true") {
            processedValue = true;
          } else if (f.value === "false") {
            processedValue = false;
          }
          return {
            field: f.field,
            op: f.op,
            value: processedValue,
            target_table: f?.target_table,
          };
        }),
        filter_logic: filterLogic,
        entity_type: null,
        page: 1,
        page_size: 10000,
      };

      let allItems = [];
      let currentPage = 1;
      let hasNext = true;

      while (hasNext) {
        const response = await getEntitiesRds({
          ...searchRequest,
          page: currentPage,
          include_total: currentPage === 1,
        });

        const data = response.data || {};
        if (Array.isArray(data.entities)) {
          allItems = [...allItems, ...data.entities];
        }

        hasNext = Boolean(data.pagination?.has_next);
        currentPage += 1;
      }

      if (setPostgresFilteredEntities) {
        setPostgresFilteredEntities(flattenEntities(allItems));
      }
    } catch (error) {
      if (setPostgresFilteredEntities) {
        setPostgresFilteredEntities(null);
      }
    } finally {
      if (setIsSearching) {
        setIsSearching(false);
      }
    }
  };

  const handleClearPostgresFilters = () => {
    setPostgresFilters([]);
    if (setPostgresFilteredEntities) {
      setPostgresFilteredEntities(null);
    }
    if (onRdsSearchClear) {
      onRdsSearchClear();
    }
  };

  const formatFilterDisplay = (filter) => {
    const formatValue = () => {
      if (
        filter?.target_table === FILTER_TARGET_TABLE.PROGRAMS &&
        filter?.value
      ) {
        const name = filter.value.name || "";
        const version = filter.value.version || "Any version";
        return [name, version].filter(Boolean).join(" / ");
      }
      if (Array.isArray(filter?.value)) {
        return filter.value.join(", ");
      }
      return filter?.value ?? "";
    };

    if (filter?.formattedName) {
      return `${filter.formattedName}: ${formatValue()}`;
    }
    const fieldParts = filter.field.split(RAW_DATA_DELIMITER);
    let field;
    if (fieldParts.length === 2) {
      [, field] = fieldParts;
    } else {
      field = filter.field;
    }
    const formattedField = field
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return `${formattedField}: ${formatValue()}`;
  };

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      {isActiveRds && (
        <>
          <EntityFilter
            type={ALL_ENTITIES}
            onSearch={handlePostgresSearch}
            entityType={null}
            initialFilters={entityFilters}
            initialFilterLogic={entityFilterLogic}
          />

          {postgresFilters.length > 0 && (
            <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
              <Typography variant="overline">Active Filters:</Typography>
              {postgresFilters.map((filter, index) => (
                <SmartFilterButton
                  key={index}
                  text={formatFilterDisplay(filter)}
                  onRemove={handleClearPostgresFilters}
                  selected={true}
                  disabled={false}
                />
              ))}
            </Box>
          )}
        </>
      )}
      {!isActiveRds && (
        <Box display="flex" flexWrap="wrap" alignItems="flex-start" gap={1}>
          <Box width="300px">
            <SearchInput
              fieldName={filterName.SEARCH_STRING}
              appliedFilters={appliedFilters}
              setAppliedFilters={setAppliedFilters}
              minSearchLength={3}
              placeholder="Search entities..."
            />
          </Box>
          {filters.map((filter, index) => (
            <Box key={index} width="min-content">
              {filter}
            </Box>
          ))}
          <ClearFiltersButton
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
      )}
    </Box>
  );
}
